import { useTheme, cssVar } from '../context/ThemeContext';

// One shared visual language for every Recharts chart in the app: a single
// brand hue (violet) instead of unrelated per-category colors, a near-
// invisible grid, no hard axis borders, thin rounded trend lines, subtle
// area gradients, a "current value" dot instead of one per point, and a
// tooltip that matches the rest of the UI instead of Recharts' white
// default. Centralized here so the next chart just imports these instead of
// re-deriving the same values.
//
// Recharts props like `stroke`/`fill` need literal color strings — they
// can't resolve a CSS custom property the way a Tailwind class can. The
// consumer app reads them per theme via useChartTheme() (bottom of file);
// the hex constants just below are the dark-only originals, still used by
// the always-dark /admin panel.

// Brand violet — same hue as --primary in index.css (258 90% 66%, Tailwind's
// violet-500, #8b5cf6). Every multi-series chart below differentiates series
// by *opacity* of this one color instead of giving each its own hue, so
// several bars/slices read as "the same kind of thing, more or less of it"
// rather than as unrelated categories competing for attention.
export const CHART_HUE = '#8b5cf6';

// Exception to the single-hue rule: income vs. expense keeps the app-wide
// semantic green/red (same colors Dashboard, TransactionList, etc. already
// use for the same distinction) instead of two violet opacities — losing
// that at-a-glance "money in vs. money out" cue wasn't worth the
// consistency win for this one pairing.
export const CHART_INCOME_COLOR = '#10b981';
export const CHART_EXPENSE_COLOR = '#f43f5e';

// Opacity ladder for ranking series/categories by magnitude within one
// chart (largest → smallest). Index 0 = most prominent. Clamps to the last
// step if a chart ever has more series than steps defined here.
const OPACITY_STEPS = [1, 0.82, 0.64, 0.46, 0.28];

export function chartColorAt(index) {
  const step = OPACITY_STEPS[Math.min(index, OPACITY_STEPS.length - 1)];
  return withAlpha(CHART_HUE, step);
}

function withAlpha(hex, opacity) {
  const alpha = Math.round(opacity * 255).toString(16).padStart(2, '0');
  return `${hex}${alpha}`;
}

// CartesianGrid — horizontal lines only, ~5% white. (The brief's own example
// value, stroke="#ffffff0d", renders at ~5%, a bit under the "~8-10%" also
// mentioned alongside it — the literal hex was treated as the source of
// truth since it's the more precise of the two.)
export const chartGridProps = {
  horizontal: true,
  vertical: false,
  stroke: '#ffffff0d',
};

// Shared XAxis/YAxis look: no axis line, no tick line, small muted label
// text set explicitly to the project's body font (Inter) instead of
// whatever Recharts defaults to.
export const chartAxisProps = {
  axisLine: false,
  tickLine: false,
  tick: { fill: '#64748b', fontSize: 11, fontFamily: 'Inter, sans-serif' },
};

// Trend-line look for Area/Line series.
export const chartLineProps = {
  stroke: CHART_HUE,
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

// Subtle top-to-bottom fade for area fills (28% opacity at the top, down to
// 0% at the bottom) instead of a flat, fully-opaque fill.
export function chartAreaGradient(id, color = CHART_HUE) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity={0.28} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

// Dot renderer for Area/Line charts that draws a marker only on the last
// data point (the "current value"), not one per point. Recharts calls this
// once per rendered point via the `dot` prop; we return null for all but
// the final index.
export function lastPointDot(dataLength, color = CHART_HUE) {
  return (props) => {
    const { cx, cy, index } = props;
    if (index !== dataLength - 1 || cx == null || cy == null) return null;
    return <circle cx={cx} cy={cy} r={4} fill={color} stroke="#0f172a" strokeWidth={2} />;
  };
}

// Tooltip that matches the app's dark cards instead of Recharts' white
// default. Drop-in replacement for <Tooltip content={...} />.
// `formatValue` lets callers pass formatCurrency, a percentage formatter, etc.
// `formatLabel` turns the raw x key ("2026-10-25") into a human label.
export function ChartTooltip({ active, payload, label, formatValue = (v) => v, formatLabel = (l) => l }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 shadow-xl">
      {label && <p className="text-xs text-slate-400 mb-1">{formatLabel(label)}</p>}
      {payload.map((entry, i) => (
        <p key={i} className="text-xs font-medium" style={{ color: entry.color }}>
          {entry.name}: {formatValue(entry.value)}
        </p>
      ))}
    </div>
  );
}

// Theme-aware version of everything above, for the consumer app (light and
// dark — redesign.md, Fase 2). Colors are read from the CSS theme tokens on
// each render, so a theme switch re-renders charts with the right palette.
export function useChartTheme() {
  useTheme();  // re-render on theme change
  const rgb = (name) => cssVar(name);
  const hueRgb = rgb('--brand-rgb');
  const hue = `rgb(${hueRgb})`;
  const textMuted = `rgb(${rgb('--text-2-rgb')})`;
  const textRgb = rgb('--text-rgb');
  return {
    hue,
    income: `rgb(${rgb('--success-rgb')})`,
    expense: `rgb(${rgb('--danger-rgb')})`,
    colorAt: (index) => `rgb(${hueRgb} / ${OPACITY_STEPS[Math.min(index, OPACITY_STEPS.length - 1)]})`,
    gridProps: { ...chartGridProps, stroke: `rgb(${textRgb} / 0.06)` },
    axisProps: { ...chartAxisProps, tick: { ...chartAxisProps.tick, fill: textMuted } },
    lineProps: { ...chartLineProps, stroke: hue },
    cursorFill: `rgb(${textRgb} / 0.04)`,
    areaGradient: (id) => chartAreaGradient(id, hue),
    lastPointDot: (dataLength) => {
      const surface = cssVar('--card');
      return (props) => {
        const { cx, cy, index } = props;
        if (index !== dataLength - 1 || cx == null || cy == null) return null;
        return <circle cx={cx} cy={cy} r={4} fill={hue} stroke={surface} strokeWidth={2} />;
      };
    },
  };
}
